/**
 * Wizard state + context. The Modal and Navigator components share state
 * via this provider so merchant pages can render the step selector
 * OUTSIDE the preview card.
 */
import { useMemo, useState, useEffect, useCallback, createContext, useContext } from "react";
import {
    loadFmsProfile,
    saveFmsProfile,
    PREVIEW_PROFILE_STUBS,
} from "@/lib/find-my-size-profile";
import { BODY_ORDER, buildVisibleSteps } from "./constants";

export const WizardCtx = createContext(null);

export const WizardPreviewProvider = ({
    accent,
    settings,
    modalTitle,
    requiredBodyFields,
    widgetTemplate,
    widgetTokens,
    sizeTableButtonText,
    onClose,
    onOpenSizeChart,
    liveSiteKey,
    liveSku,
    liveApiBase,
    children,
}) => {
    // Normalize the required body fields prop → stable, deduped, in BODY_ORDER +
    // 'height' preserved at the end. BODY_ORDER kapsamayan ("height") kind'ı
    // adım listesine değil ama `effectiveRequiredBodyFields` sinyaline dahil
    // ederiz — BasicInfoStep "Zorunlu" rozetini bu sinyale bakarak gösterir.
    // Falls back to all four body kinds if nothing was passed (admin panel preview).
    const effectiveRequiredBodyFields = useMemo(
        () => {
            if (!Array.isArray(requiredBodyFields) || !requiredBodyFields.length) {
                return [...BODY_ORDER];
            }
            const set = new Set(requiredBodyFields.filter((k) => BODY_ORDER.includes(k)));
            const ordered = BODY_ORDER.filter((k) => set.has(k));
            // 'height' body steps üretmez (basic adımı zaten boy alır) ama
            // product_length tablolarında rozet için sinyal taşır.
            if (requiredBodyFields.includes("height")) {
                ordered.push("height");
            }
            return ordered;
        },
        [requiredBodyFields],
    );
    // The actual wizard flow — welcome / basic / fit / [body steps] / result.
    // Rebuilt whenever the merchant's required measurements change.
    const visibleSteps = useMemo(
        () => buildVisibleSteps(effectiveRequiredBodyFields),
        [effectiveRequiredBodyFields],
    );
    // Ziyaretçinin gerçek profili (localStorage). Merchant panelde bir senaryo
    // seçmediği sürece bu kullanılır.
    const [profile, setProfile] = useState(() => loadFmsProfile());
    // Live mode (merchant sitesindeki widget): Smart Recall start-step
    // kararı. `requiredBodyFields` burada henüz tamam değilse welcome'ı
    // seçmek yine güvenli — welcomeMode zaten profile uygunluğunu bir sonraki
    // render'da yeniden hesaplar. Admin panel preview'ı (`!liveMode`) her
    // zaman basic'ten açılır — merchant recall senaryolarını üstteki
    // pill'lerle test ediyor.
    const liveModeInit = !!(liveSiteKey && liveSku);
    const smartRecallInit = settings?.smartRecall || { enabled: true, fastPathMode: "auto_recommend" };
    const [step, setStep] = useState(() => {
        const idxBasic = Math.max(0, visibleSteps.indexOf("basic"));
        if (!liveModeInit) return idxBasic;
        const seed = loadFmsProfile();
        if (!seed || !(seed.height || seed.weight || seed.age || seed.fit)) {
            // No cached data at all → start the questionnaire fresh.
            return idxBasic;
        }
        if (smartRecallInit.enabled === false) {
            // Smart Recall off → treat every visit as a cold start, even if
            // we have cached data from an earlier session.
            return idxBasic;
        }
        // Smart Recall on, profile has at least some data → welcome handles
        // all three recall scenarios in a single step:
        //   • "partial"  → inline mini-question for the missing field
        //   • "confirm"  → "Bilgilerin hâlâ doğru mu?" (always_confirm mode)
        //   • "recommend"→ "Seni hatırlıyorum, önerim: M" (auto_recommend
        //                   mode — the big 72 px size letter + direct
        //                   "Bedeni Seç" CTA that auto-selects on the host
        //                   DOM, no extra tap through Result)
        // The result step is reserved for flows that walk through a real
        // wizard: cold-start questionnaire OR a partial recall that just
        // answered the last missing measurement. A cached-complete visitor
        // never sees Result — the welcome recommend screen IS the final
        // destination, keeping the "we remembered you" moment front-and-
        // center instead of jumping to a bare result card.
        return Math.max(0, visibleSteps.indexOf("welcome"));
    });
    const [previewRefusal, setPreviewRefusal] = useState(false);
    // Preview-only: 3 Smart Recall senaryosunu simüle etmek için stub override.
    // null = gerçek profil kullan; "empty"/"partial"/"complete" = stub kullan.
    const [recallScenario, setRecallScenario] = useState(null);
    // Stub modundayken kullanıcının verdiği yeni cevaplar — localStorage'a
    // dokunmadan stub üzerine merge edilir. Böylece merchant "Kısmi"de bel'i
    // cevaplayınca boy/kilo/yaş chip'leri kaybolmaz.
    const [stubOverrides, setStubOverrides] = useState({});
    // En son cevaplanan alan — Kısmi'de yeni chip'i vurgulamak için (dark bg).
    // Yeni scenario / reset / adım değişimi ile temizlenir.
    const [lastAnsweredField, setLastAnsweredField] = useState(null);

    // Clamp `step` whenever the visible step list shrinks (merchant changed
    // the table's measures mid-session). Keeps the wizard from landing on a
    // step that no longer exists.
    useEffect(() => {
        setStep((s) => Math.min(Math.max(0, s), visibleSteps.length - 1));
    }, [visibleSteps]);

    const setField = useCallback((key, value) => {
        setLastAnsweredField(key);
        if (recallScenario) {
            // Stub modu: sadece bellekteki override'a ekle, localStorage'a yazma
            setStubOverrides((prev) => ({ ...prev, [key]: value }));
        } else {
            // Gerçek profil modu: localStorage'a persist et
            const next = saveFmsProfile({ [key]: value });
            setProfile((prev) => next || { ...prev, [key]: value });
        }
    }, [recallScenario]);

    const goTo = useCallback((idx, refusal = false) => {
        setStep(Math.max(0, Math.min(visibleSteps.length - 1, idx)));
        setPreviewRefusal(refusal);
        setLastAnsweredField(null);
    }, [visibleSteps]);
    const goNext = useCallback(() => {
        setStep((s) => Math.min(visibleSteps.length - 1, s + 1));
        setPreviewRefusal(false);
    }, [visibleSteps]);
    const goBack = useCallback(() => {
        setStep((s) => Math.max(0, s - 1));
        setPreviewRefusal(false);
        setLastAnsweredField(null);
    }, []);
    const goReset = useCallback(() => {
        // "Bilgilerimi Değiştir" visitor's existing profile is KEPT INTACT —
        // we only drive them back to the start of the wizard so they can
        // tweak whichever field they want to update. Clearing the profile
        // used to be a trap: if the merchant's table only requires `waist`
        // (height/weight/age + fit + waist), the fresh wizard would drop
        // the fields we knew from previous visits (chest/hip/shoulder) and
        // ship an incomplete profile to the suggester, which then couldn't
        // score reliably and bounced back as Nazik Ret even though the
        // visitor hadn't actually changed anything. Keeping the profile +
        // localStorage lets setField update only the field the visitor
        // interacts with; everything else round-trips untouched.
        setRecallScenario(null);
        setStubOverrides({});
        setLastAnsweredField(null);
        setStep(Math.max(0, visibleSteps.indexOf("basic")));
        setPreviewRefusal(false);
    }, [visibleSteps]);
    const goToStepId = useCallback((id) => {
        const idx = visibleSteps.indexOf(id);
        if (idx >= 0) {
            setStep(idx);
            setPreviewRefusal(false);
        }
    }, [visibleSteps]);
    const activateRecallScenario = useCallback((scenario) => {
        setRecallScenario(scenario);
        setStubOverrides({}); // Yeni senaryoya geçerken override'ları temizle
        setLastAnsweredField(null);
        setStep(0);
        setPreviewRefusal(false);
    }, []);

    // Aktif profil — stub aktifse (stub + overrides), değilse gerçek profil
    const effectiveProfile = useMemo(() => {
        if (recallScenario && PREVIEW_PROFILE_STUBS[recallScenario]) {
            return { ...PREVIEW_PROFILE_STUBS[recallScenario], ...stubOverrides };
        }
        return profile;
    }, [recallScenario, stubOverrides, profile]);

    const value = useMemo(
        () => ({
            accent, settings, modalTitle,
            profile: effectiveProfile,
            setField,
            step, previewRefusal,
            goTo, goNext, goBack, goReset, goToStepId,
            recallScenario, activateRecallScenario,
            requiredBodyFields: effectiveRequiredBodyFields,
            visibleSteps,
            lastAnsweredField,
            widgetTemplate: widgetTemplate || "minimal",
            widgetTokens: widgetTokens || { accent },
            sizeTableButtonText: sizeTableButtonText || "Beden Tablosu",
            onClose: typeof onClose === "function" ? onClose : null,
            // Live backend bağlantısı: siteKey + sku varsa backend algoritmasına
            // POST atılır. Yoksa dashboard preview (stub) mantığı korunur.
            liveSiteKey: liveSiteKey || null,
            liveSku: liveSku || null,
            liveApiBase: liveApiBase || (typeof window !== "undefined" ? window.location.origin : ""),
            onOpenSizeChart: onOpenSizeChart || null,
        }),
        [accent, settings, modalTitle, effectiveProfile, setField, step, previewRefusal, goTo, goNext, goBack, goReset, goToStepId, recallScenario, activateRecallScenario, effectiveRequiredBodyFields, visibleSteps, lastAnsweredField, widgetTemplate, widgetTokens, sizeTableButtonText, onClose, onOpenSizeChart, liveSiteKey, liveSku, liveApiBase],
    );

    return <WizardCtx.Provider value={value}>{children}</WizardCtx.Provider>;
};

export const useWizardCtx = () => {
    const ctx = useContext(WizardCtx);
    if (!ctx) throw new Error("WizardPreview compound components must be wrapped in <WizardPreviewProvider>.");
    return ctx;
};
